<template>
  <gas-page-shell :breadcrumb="['用户管理', '角色权限']">
  <div class="page-inner">
    <div class="page-head">
      <div class="table-title-group">
        <h3>角色管理</h3>
        <gas-table-toolbar
          :columns="tableColumnDefs"
          :visible-map.sync="tableColumnVisible"
          :selected-count="selectedIds.length"
          :show-batch-delete="tableCanBatchDelete"
          @batch-delete="batchRemove"
        />
      </div>
      <el-button type="primary" size="small" @click="openCreate">新增角色</el-button>
    </div>

    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="角色名称">
        <el-input v-model="query.roleName" clearable placeholder="请输入角色名称" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" v-loading="loading" border size="small" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="44" align="center" />
      <el-table-column prop="roleId" label="ID" width="80" />
      <el-table-column prop="roleName" label="角色名称" min-width="160" />
      <el-table-column prop="roleKey" label="权限字符" min-width="160" />
      <el-table-column prop="remark" label="备注" min-width="180" show-overflow-tooltip />
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <gas-status-tag kind="org" :value="scope.row.status" />
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" min-width="160" />
      <el-table-column label="管理" width="230" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)">修改</el-button>
          <el-button type="text" size="small" @click="openPermission(scope.row)">权限</el-button>
          <el-button
            type="text"
            size="small"
            class="danger"
            :disabled="scope.row.roleKey === 'admin'"
            @click="remove(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next"
      :total="total"
      :page-size="query.pageSize"
      :current-page="query.pageNum"
      :page-sizes="[10, 20, 50, 100]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px">
      <el-form :model="form" label-width="90px" size="small">
        <el-form-item label="角色名称">
          <el-input v-model="form.roleName" placeholder="例如：运营管理员" />
        </el-form-item>
        <el-form-item label="权限字符">
          <el-input v-model="form.roleKey" placeholder="例如：operator" :disabled="form.roleKey === 'admin'" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio label="0">正常</el-radio>
            <el-radio label="1">停用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible=false">取消</el-button>
        <el-button type="primary" @click="submit">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="permissionTitle" :visible.sync="permissionVisible" width="560px">
      <div class="tree-toolbar">
        <el-button size="mini" @click="checkAll">全选</el-button>
        <el-button size="mini" @click="clearAll">清空</el-button>
      </div>
      <el-tree
        ref="menuTree"
        :data="menuTree"
        :props="{ label: 'label', children: 'children' }"
        node-key="id"
        show-checkbox
        default-expand-all
      />
      <span slot="footer">
        <el-button @click="permissionVisible=false">取消</el-button>
        <el-button type="primary" @click="submitPermission">保存权限</el-button>
      </span>
    </el-dialog>
  </div>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import gasTableEnhance from '@/mixins/gasTableEnhance'
import { roleApi, menuApi } from '@/api/system'

const emptyForm = () => ({
  roleId: null,
  roleName: '',
  roleKey: '',
  status: '0',
  remark: ''
})

const emptyQuery = () => ({ pageNum: 1, pageSize: 10, roleName: '' })

export default {
  components: { GasPageShell },
  mixins: [gasTableEnhance],
  data() {
    return {
      idField: 'roleId',
      removeApi: roleApi.remove,
      batchRemoveApi: roleApi.batchRemove,
      loading: false,
      list: [],
      total: 0,
      query: emptyQuery(),
      dialogVisible: false,
      dialogTitle: '新增角色',
      form: emptyForm(),
      permissionVisible: false,
      permissionTitle: '菜单权限',
      currentRole: null,
      menuTree: []
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await roleApi.list(this.query)
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.query = emptyQuery()
      this.loadData()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    openCreate() {
      this.dialogTitle = '新增角色'
      this.form = emptyForm()
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = '修改角色'
      this.form = { ...emptyForm(), ...row }
      this.dialogVisible = true
    },
    async submit() {
      if (!this.form.roleName || !this.form.roleKey) {
        this.$message.warning('请填写角色名称和权限字符')
        return
      }
      if (this.form.roleId) await roleApi.update(this.form)
      else await roleApi.create(this.form)
      this.$message.success('保存成功')
      this.dialogVisible = false
      this.loadData()
    },
    async remove(row) {
      await this.$confirm(`确认删除角色 ${row.roleName}？`, '提示', { type: 'warning' })
      await roleApi.remove(row.roleId)
      this.$message.success('删除成功')
      this.loadData()
    },
    async openPermission(row) {
      this.currentRole = row
      this.permissionTitle = `菜单权限 - ${row.roleName}`
      this.permissionVisible = true
      const treeRes = await menuApi.tree()
      const idsRes = await roleApi.menuIds(row.roleId)
      this.menuTree = treeRes.data || []
      this.$nextTick(() => {
        this.$refs.menuTree.setCheckedKeys(this.checkedLeafIds(this.menuTree, idsRes.data || []))
      })
    },
    async submitPermission() {
      const tree = this.$refs.menuTree
      const menuIds = [...tree.getCheckedKeys(), ...tree.getHalfCheckedKeys()]
      await roleApi.updateMenus(this.currentRole.roleId, menuIds)
      this.$message.success('权限保存成功，相关用户刷新后生效')
      this.permissionVisible = false
    },
    checkAll() {
      const ids = []
      const walk = nodes => {
        nodes.forEach(node => {
          ids.push(node.id)
          if (node.children && node.children.length) walk(node.children)
        })
      }
      walk(this.menuTree)
      this.$refs.menuTree.setCheckedKeys(ids)
    },
    clearAll() {
      this.$refs.menuTree.setCheckedKeys([])
    },
    checkedLeafIds(nodes, selectedIds) {
      const selected = new Set(selectedIds)
      const ids = []
      const walk = list => {
        list.forEach(node => {
          const children = node.children || []
          if (children.length) walk(children)
          else if (selected.has(node.id)) ids.push(node.id)
        })
      }
      walk(nodes)
      return ids
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-head h3 { margin: 0; }
.search-form { margin-bottom: 12px; }
.pager { margin-top: 16px; text-align: right; }
.danger { color: #f56c6c; }
.tree-toolbar { margin-bottom: 12px; }
</style>
